<script lang="ts">
  import type {
    Group,
    NameFrequency,
    SearchUngroupedBaseSchema,
    SearchUngroupedBody,
    Variation,
  } from '@pin/schemas/admin';
  import { SvelteMap } from 'svelte/reactivity';
  import {
    allGroups$,
    allVariations$,
    deleteGroup,
    fetchGroupVariationCounts,
    searchUngrouped,
    updateGroup,
  } from '../../api/group';
  import { GROUP_COUNTS_POLL_MS } from '../../lib/constants';
  import { scrollFade } from '../../lib/scrollFade';
  import { addToast } from '../../stores/toastStore';
  import ConfirmDialog from '../ConfirmDialog.svelte';

  const PAGE_SIZE = 50;

  const SEARCH_TYPE_OPTIONS: Array<{
    value: SearchUngroupedBody['type'];
    label: string;
  }> = [
    { value: 'fuzzy', label: 'Liknende' },
    { value: 'startsWith', label: 'Begynner med' },
    { value: 'includes', label: 'Inneholder' },
  ];

  interface Props {
    groupId: string;
    initialSearchQuery?: string;
    /** Shows a one-off "group created" celebration and guides the user to add variations. */
    justCreated?: boolean;
    onClose?: () => void;
    onDelete?: () => void;
    onGroupNameChange?: (name: string | undefined) => void;
  }

  let {
    groupId,
    initialSearchQuery = '',
    justCreated = false,
    onClose,
    onDelete,
    onGroupNameChange,
  }: Props = $props();

  let groups = $state<Group[] | null>(null);
  let variations = $state<Variation[] | null>(null);
  let variationCounts = new SvelteMap<string, number>();

  const group = $derived(groups?.find((g) => g.id === groupId));
  const groupVariations = $derived(
    variations?.filter((v) => v.groupId === groupId) ?? []
  );
  const totalCount = $derived(
    groupVariations.reduce(
      (sum, v) => sum + (variationCounts.get(v.value) ?? 0),
      0
    )
  );

  $effect(() => {
    onGroupNameChange?.(group?.name);
  });

  const isLoading = $derived(groups == null || variations == null);
  const isMissing = $derived(groups != null && group == null);

  let isEditingName = $state(false);
  let nameDraft = $state('');
  let isSavingName = $state(false);

  let variationToRemove = $state<Variation | null>(null);

  let showDeleteConfirm = $state(false);
  let isDeleting = $state(false);

  // Add variations state
  let searchQuery = $state('');
  let searchType = $state<SearchUngroupedBody['type']>('fuzzy');
  let ungroupedNames = $state<NameFrequency[]>([]);
  let selectedNames = $state<string[]>([]);
  let isSearching = $state(false);
  let searchFailed = $state(false);
  let loadMoreFailed = $state(false);
  let hasSearched = $state(false);
  let hasMore = $state(false);
  let pageNum = $state(1);
  let isAddingVariations = $state(false);

  const allSelected = $derived(
    ungroupedNames.length > 0 &&
      ungroupedNames.every((n) => selectedNames.includes(n.normalizedName))
  );
  const someSelected = $derived(selectedNames.length > 0 && !allSelected);

  const refreshVariationCounts = async () => {
    try {
      const counts = await fetchGroupVariationCounts(groupId);
      variationCounts.clear();
      for (const { normalizedName, count } of counts) {
        variationCounts.set(normalizedName, count);
      }
    } catch (e) {
      // Background poll: keep the last counts on screen instead of toasting.
      console.error(e);
    }
  };

  const startEditName = () => {
    if (!group) return;
    nameDraft = group.name;
    isEditingName = true;
  };

  const cancelEditName = () => {
    isEditingName = false;
    nameDraft = '';
  };

  const saveName = async () => {
    if (!group || nameDraft.trim().length === 0) return;
    if (nameDraft.trim() === group.name) {
      cancelEditName();
      return;
    }

    isSavingName = true;
    try {
      await updateGroup(groupId, { name: nameDraft.trim() });
      addToast({ type: 'success', message: 'Gruppenavn oppdatert' });
      isEditingName = false;
      nameDraft = '';
    } catch (e) {
      console.error(e);
      addToast({ type: 'error', message: `Kunne ikke oppdatere navn` });
    }
    isSavingName = false;
  };

  const handleRemoveVariation = async () => {
    if (!variationToRemove) return;

    try {
      await updateGroup(groupId, {
        variationOperations: [
          { type: 'remove', variationId: variationToRemove.id },
        ],
      });
      addToast({ type: 'success', message: 'Variant fjernet' });
    } catch (e) {
      console.error(e);
      addToast({ type: 'error', message: `Kunne ikke fjerne variant` });
    }
    variationToRemove = null;
  };

  const buildSearchBody = (q: string, page: number): SearchUngroupedBody => {
    const common: SearchUngroupedBaseSchema = {
      pageNum: page,
      pageSize: PAGE_SIZE,
    };
    switch (searchType) {
      case 'fuzzy':
        return { ...common, type: 'fuzzy', q, threshold: 0.9 };
      case 'startsWith':
        return { ...common, type: 'startsWith', q };
      case 'includes':
        return { ...common, type: 'includes', q };
    }
  };

  const handleSearch = async () => {
    const trimmedQ = searchQuery.trim();
    if (trimmedQ.length === 0) return;

    isSearching = true;
    searchFailed = false;
    loadMoreFailed = false;
    hasSearched = true;
    pageNum = 1;
    selectedNames = [];

    try {
      const names = await searchUngrouped(buildSearchBody(trimmedQ, 1));
      ungroupedNames = names;
      hasMore = names.length === PAGE_SIZE;
    } catch (e) {
      // Don't leave results from a previous query looking like this one's.
      console.error(e);
      ungroupedNames = [];
      hasMore = false;
      searchFailed = true;
    }
    isSearching = false;
  };

  const handleLoadMore = async () => {
    if (isSearching || !hasMore) return;

    isSearching = true;
    loadMoreFailed = false;

    try {
      const names = await searchUngrouped(
        buildSearchBody(searchQuery.trim(), pageNum + 1)
      );
      ungroupedNames = [...ungroupedNames, ...names];
      pageNum += 1;
      hasMore = names.length === PAGE_SIZE;
    } catch (e) {
      console.error(e);
      loadMoreFailed = true;
    }
    isSearching = false;
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      selectedNames = [];
    } else {
      selectedNames = ungroupedNames.map((n) => n.normalizedName);
    }
  };

  const handleAddVariations = async () => {
    if (selectedNames.length === 0) return;

    isAddingVariations = true;
    try {
      const selectedSet = new Set(selectedNames);
      const operations = ungroupedNames
        .filter((n) => selectedSet.has(n.normalizedName))
        .map((n) => ({
          type: 'new' as const,
          value: n.normalizedName,
          displayValue: n.nameSample,
        }));
      await updateGroup(groupId, { variationOperations: operations });
      addToast({
        type: 'success',
        message: `${selectedNames.length} variant${selectedNames.length > 1 ? 'er' : ''} lagt til`,
      });
      // Remove added names from search results
      ungroupedNames = ungroupedNames.filter(
        (n) => !selectedNames.includes(n.normalizedName)
      );
      selectedNames = [];
    } catch (e) {
      console.error(e);
      addToast({
        type: 'error',
        message: `Kunne ikke legge til varianter`,
      });
    }
    isAddingVariations = false;
  };

  const handleDeleteGroup = async () => {
    isDeleting = true;
    try {
      await deleteGroup(groupId);
      addToast({ type: 'success', message: 'Gruppe slettet' });
      onDelete?.();
    } catch (e) {
      console.error(e);
      addToast({ type: 'error', message: `Kunne ikke slette gruppe` });
    }
    showDeleteConfirm = false;
    isDeleting = false;
  };

  $effect(() => {
    const sub = allGroups$.subscribe((g) => {
      groups = g;
    });
    return () => sub.unsubscribe();
  });

  $effect(() => {
    const sub = allVariations$.subscribe((v) => {
      variations = v;
    });
    return () => sub.unsubscribe();
  });

  $effect(() => {
    void refreshVariationCounts();
    const interval = setInterval(
      () => void refreshVariationCounts(),
      GROUP_COUNTS_POLL_MS
    );
    return () => clearInterval(interval);
  });

  // Auto-search from initial query prop
  $effect(() => {
    if (initialSearchQuery && !hasSearched && variations != null) {
      searchQuery = initialSearchQuery;
      handleSearch();
    }
  });
</script>

<div class="group-detail-content">
  {#if isLoading}
    <div class="loading">
      <div class="spinner"></div>
      <span>Laster gruppe...</span>
    </div>
  {:else if isMissing}
    <div class="not-found">
      <p>Fant ingen gruppe med id <code>{groupId}</code></p>
      {#if onClose}
        <button class="btn secondary" onclick={onClose}>Lukk</button>
      {/if}
    </div>
  {:else if group}
    <section class="group-header-section" class:just-created={justCreated}>
      <div class="header-row">
        {#if isEditingName}
          <div class="name-edit">
            <input
              class="name-input"
              bind:value={nameDraft}
              onkeydown={(e) => {
                if (e.key === 'Enter') saveName();
                if (e.key === 'Escape') cancelEditName();
              }}
              disabled={isSavingName}
            />
            <button
              class="btn primary"
              onclick={saveName}
              disabled={isSavingName || nameDraft.trim().length === 0}
            >
              Lagre
            </button>
            <button
              class="btn secondary"
              onclick={cancelEditName}
              disabled={isSavingName}
            >
              Avbryt
            </button>
          </div>
        {:else}
          <div class="name-display">
            <h1 class="group-title">{group.name}</h1>
            {#if justCreated}
              <span class="created-badge" role="status">
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M3.5 8.5l3 3 6-7" />
                </svg>
                Gruppe opprettet
              </span>
            {/if}
            <button class="btn tertiary edit-btn" onclick={startEditName}>
              Rediger navn
            </button>
          </div>
        {/if}
        <button
          class="btn danger"
          onclick={() => (showDeleteConfirm = true)}
          disabled={isDeleting}
        >
          Slett gruppe
        </button>
      </div>
      <p class="total-count">Totalt {totalCount} gjett</p>
    </section>

    <div class="columns">
      <!-- Left column: Existing variations -->
      <section class="variations-column">
        <h2 class="column-title">
          Varianter ({groupVariations.length})
        </h2>

        {#if groupVariations.length > 0}
          <ul
            class="variation-list scroll-fade"
            role="list"
            {@attach scrollFade}
          >
            {#each groupVariations.sort((a, b) => (variationCounts.get(b.value) ?? 0) - (variationCounts.get(a.value) ?? 0)) as variation (variation.id)}
              <li class="variation-item">
                <button
                  class="variation-value"
                  onclick={() => {
                    searchQuery = variation.value;
                    handleSearch();
                  }}
                  title="Søk etter lignende"
                >
                  {variation.displayValue}
                </button>
                <span class="variation-count">
                  {variationCounts.get(variation.value) ?? 0}
                </span>
                <button
                  class="btn clear remove-btn"
                  onclick={() => (variationToRemove = variation)}
                  title="Fjern variant"
                >
                  ×
                </button>
              </li>
            {/each}
          </ul>
        {:else}
          <p class="empty-message">Ingen varianter i denne gruppen.</p>
        {/if}
      </section>

      <!-- Right column: Add variations -->
      <section class="add-column" class:just-created={justCreated}>
        <h2 class="column-title">Legg til varianter</h2>
        {#if justCreated}
          <p class="created-hint">
            Vi har søkt etter lignende stavemåter. Huk av de som hører til
            gruppen og legg dem til.
          </p>
        {/if}

        <form
          class="search-form"
          onsubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <input
            type="search"
            class="search-input"
            bind:value={searchQuery}
            placeholder="Søk etter navn..."
          />
          <select
            class="search-type-select"
            bind:value={searchType}
            aria-label="Søketype"
          >
            {#each SEARCH_TYPE_OPTIONS as option (option.value)}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
          <button
            type="submit"
            class="btn primary"
            disabled={isSearching || searchQuery.trim().length === 0}
          >
            Søk
          </button>
          {#if hasSearched}
            <button
              type="button"
              class="btn secondary"
              onclick={() => {
                searchQuery = '';
                ungroupedNames = [];
                selectedNames = [];
                hasSearched = false;
                searchFailed = false;
                loadMoreFailed = false;
                hasMore = false;
                pageNum = 1;
              }}
            >
              Nullstill
            </button>
          {/if}
        </form>

        <div class="add-content">
          {#if !hasSearched}
            <p class="help-text">
              Søk etter ugruperte navn for å legge dem til i gruppen.
            </p>
          {:else if isSearching && ungroupedNames.length === 0}
            <div class="loading-inline">
              <div class="spinner"></div>
              <span>Søker...</span>
            </div>
          {:else if ungroupedNames.length > 0}
            <div class="results-header">
              <label class="select-all">
                <input
                  type="checkbox"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onchange={toggleSelectAll}
                />
                <span>
                  {allSelected ? 'Fjern alle' : 'Velg alle'}
                  ({ungroupedNames.length})
                </span>
              </label>
            </div>

            <div class="results-scroll scroll-fade" {@attach scrollFade}>
              <ul class="ungrouped-list" role="list">
                {#each ungroupedNames as name (name.normalizedName)}
                  <li class="ungrouped-item">
                    <label class="ungrouped-label">
                      <input
                        type="checkbox"
                        bind:group={selectedNames}
                        value={name.normalizedName}
                      />
                      <span class="ungrouped-text">{name.nameSample}</span>
                      <span class="ungrouped-count">{name.count}</span>
                    </label>
                  </li>
                {/each}
              </ul>

              {#if hasMore}
                {#if loadMoreFailed}
                  <p class="empty-message" role="alert">
                    Kunne ikke laste flere.
                  </p>
                {/if}
                <button
                  class="btn secondary load-more"
                  onclick={handleLoadMore}
                  disabled={isSearching}
                >
                  {isSearching
                    ? 'Laster...'
                    : loadMoreFailed
                      ? 'Prøv igjen'
                      : 'Last inn flere'}
                </button>
              {/if}
            </div>
          {:else if searchFailed}
            <p class="empty-message" role="alert">
              Søket feilet.
              <button
                type="button"
                class="btn secondary"
                onclick={handleSearch}
                disabled={isSearching}
              >
                Prøv igjen
              </button>
            </p>
          {:else}
            <p class="empty-message">Ingen treff på søket.</p>
          {/if}
        </div>

        <div class="add-footer">
          {#if onClose}
            <button class="btn secondary" type="button" onclick={onClose}>
              Avbryt
            </button>
          {/if}
          <button
            class="btn primary"
            onclick={handleAddVariations}
            disabled={isAddingVariations || selectedNames.length === 0}
          >
            Legg til {selectedNames.length > 0
              ? `${selectedNames.length} variant${selectedNames.length > 1 ? 'er' : ''}`
              : 'varianter'}
          </button>
        </div>
      </section>
    </div>
  {/if}
</div>

<ConfirmDialog
  open={variationToRemove != null}
  title="Fjern variant"
  message={`Er du sikker på at du vil fjerne "${variationToRemove?.displayValue ?? ''}" fra gruppen?`}
  onConfirm={handleRemoveVariation}
  onClose={() => (variationToRemove = null)}
/>

<ConfirmDialog
  open={showDeleteConfirm}
  title="Slett gruppe"
  message={`Er du sikker på at du vil slette gruppen "${group?.name}"? Alle varianter vil bli frigitt.`}
  danger
  onConfirm={handleDeleteGroup}
  onClose={() => (showDeleteConfirm = false)}
/>

<style>
  .group-detail-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-block-size: 0;
  }

  .header-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2) var(--space-4);
  }

  .loading {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-8);
    color: var(--text-muted);
  }

  .spinner {
    inline-size: 1.25rem;
    block-size: 1.25rem;
    border: 2px solid var(--border);
    border-block-start-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .not-found {
    text-align: center;
    padding: var(--space-8);

    & p {
      margin-block-end: var(--space-4);
      color: var(--text-muted);
    }

    & code {
      font-family: var(--font-mono);
      background: var(--surface-raised);
      padding: var(--space-1) var(--space-2);
      border-radius: var(--radius-sm);
    }
  }

  .group-header-section {
    margin-block-end: var(--space-4);
    flex-shrink: 0;
  }

  .name-display {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .group-title {
    font-size: var(--text-xl);
    margin: 0;
  }

  .edit-btn {
    font-size: var(--text-sm);
  }

  /* "Just created" celebration: badge pops in, header glows once, then the
     add column pulses to show where to continue. */
  .group-header-section.just-created {
    border-radius: var(--radius-lg);
    padding: var(--space-3);
    margin-inline: calc(-1 * var(--space-3));
    animation: created-glow 1.6s ease-out both;
  }

  .created-badge {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-1) var(--space-3);
    border-radius: 999px;
    background: var(--success-subtle);
    color: var(--success);
    font-size: var(--text-sm);
    font-weight: var(--nrk-sans-weight-bold);
    animation: badge-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;

    & svg {
      inline-size: 1rem;
      block-size: 1rem;
      fill: none;
      stroke: currentColor;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 14;
      stroke-dashoffset: 14;
      animation: check-draw 0.35s ease-out 0.45s forwards;
    }
  }

  .add-column.just-created {
    animation: column-pulse 1.4s ease-out 0.9s both;
  }

  .created-hint {
    margin: 0 0 var(--space-3);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-md);
    background: var(--accent-subtle);
    font-size: var(--text-sm);
    animation: hint-in 0.4s ease-out 1s both;
  }

  @keyframes created-glow {
    0% {
      background: var(--success-subtle);
      box-shadow: 0 0 0 0 var(--success);
    }
    30% {
      box-shadow: 0 0 0 4px color-mix(in srgb, var(--success) 40%, transparent);
    }
    100% {
      background: transparent;
      box-shadow: 0 0 0 12px transparent;
    }
  }

  @keyframes badge-pop {
    from {
      opacity: 0;
      transform: scale(0.5);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes check-draw {
    to {
      stroke-dashoffset: 0;
    }
  }

  @keyframes column-pulse {
    0% {
      box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 50%, transparent);
      border-color: var(--accent);
    }
    70% {
      box-shadow: 0 0 0 10px transparent;
      border-color: var(--accent);
    }
    100% {
      box-shadow: 0 0 0 0 transparent;
    }
  }

  @keyframes hint-in {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .group-header-section.just-created,
    .created-badge,
    .created-badge svg,
    .add-column.just-created,
    .created-hint {
      animation: none;
    }
    .created-badge svg {
      stroke-dashoffset: 0;
    }
  }

  .name-edit {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-block-end: var(--space-2);
  }

  .name-input {
    max-inline-size: 20rem;
    font-size: var(--text-lg);
    font-weight: var(--nrk-sans-weight-bold);
  }

  .total-count {
    color: var(--text-muted);
    margin-block-start: var(--space-1);
    font-size: var(--text-sm);
  }

  .columns {
    display: grid;
    grid-template-columns: 2fr 4fr;
    gap: var(--space-4);
    flex: 1;
    min-height: 0;
    overflow: hidden;

    /* Stacked: search gets most of the height, variations the rest. Both
       lists scroll internally so the add button stays in view; the dialog
       scrolls as a fallback on very short screens. */
    @media (width < 50rem) {
      grid-template-columns: 1fr;
      grid-template-rows: minmax(24rem, 3fr) minmax(12rem, 2fr);
      overflow: visible;
    }
  }

  .variations-column,
  .add-column {
    display: flex;
    flex-direction: column;
    background: var(--surface-raised);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    padding: var(--space-4);
    min-block-size: 0;
    overflow: hidden;
  }

  .add-column {
    @media (width < 50rem) {
      order: -1;
    }
  }

  /* Fade out whichever edge has hidden content, driven by scrollFade. */
  .scroll-fade {
    --fade-top: 0rem;
    --fade-bottom: 0rem;
    overflow-y: auto;
    overscroll-behavior: contain;
    mask-image: linear-gradient(
      to bottom,
      transparent,
      #000 var(--fade-top),
      #000 calc(100% - var(--fade-bottom)),
      transparent
    );

    &:global([data-more-above]) {
      --fade-top: var(--space-10);
    }

    &:global([data-more-below]) {
      --fade-bottom: var(--space-10);
    }
  }

  .column-title {
    font-size: var(--text-base);
    font-weight: var(--nrk-sans-weight-bold);
    margin: 0 0 var(--space-3);
    flex-shrink: 0;
  }

  .variation-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    flex: 1;
    min-block-size: 0;
  }

  .variation-item {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    background: var(--surface);
    border-radius: var(--radius-md);
    flex-shrink: 0;
  }

  .variation-value {
    flex: 1;
    font-size: var(--text-sm);
    border: none;
    background: none;
    padding: 0;
    text-align: start;
    color: var(--text);
    cursor: pointer;

    &:hover {
      color: var(--accent);
      text-decoration: underline;
    }
  }

  .variation-count {
    font-size: var(--text-xs);
    color: var(--text-muted);
    min-inline-size: 2rem;
    text-align: end;
  }

  .remove-btn {
    font-size: var(--text-base);
    color: var(--text-muted);
    padding: var(--space-1);
    line-height: 1;

    &:hover {
      color: var(--danger);
    }
  }

  .empty-message {
    padding: var(--space-4);
    text-align: center;
    color: var(--text-muted);
    font-size: var(--text-sm);
  }

  /* Wraps on narrow widths: the input keeps a usable width on its own row,
     the select stretches to fill the row below it. */
  .search-form {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  .search-input {
    flex: 999 1 12rem;
    min-inline-size: 0;
  }

  /* Override the global `inline-size: 100%` on fields so flex sizing applies. */
  .search-type-select {
    inline-size: auto;
    flex: 1 0 auto;
  }

  /* Only the results list shrinks; messages keep their height and the whole
     area scrolls as a fallback when even those don't fit. */
  .add-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-block-size: 0;
    overflow-y: auto;
    margin-block: var(--space-3);

    & > :not(.results-scroll) {
      flex-shrink: 0;
    }
  }

  .results-scroll {
    flex: 1;
    min-block-size: 0;
  }

  .add-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: var(--space-2);
    flex-shrink: 0;
    padding-block-start: var(--space-3);
    border-block-start: 1px solid var(--border-subtle);
  }

  .help-text {
    color: var(--text-muted);
    font-size: var(--text-sm);
    text-align: center;
    padding: var(--space-6);
  }

  .loading-inline {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    padding: var(--space-6);
    color: var(--text-muted);
    font-size: var(--text-sm);
  }

  .results-header {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    margin-block-end: var(--space-3);
    padding-block-end: var(--space-3);
    border-block-end: 1px solid var(--border-subtle);
    flex-shrink: 0;
  }

  .select-all {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    cursor: pointer;
  }

  .ungrouped-list {
    display: flex;
    flex-direction: column;
  }

  .ungrouped-item {
    border-block-end: 1px solid var(--border-subtle);

    &:last-child {
      border-block-end: none;
    }
  }

  .ungrouped-label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-1);
    cursor: pointer;
    font-size: var(--text-sm);

    &:hover {
      background: var(--surface-hover);
    }
  }

  .ungrouped-text {
    flex: 1;
  }

  .ungrouped-count {
    font-size: var(--text-xs);
    color: var(--text-muted);
  }

  .load-more {
    inline-size: 100%;
    margin-block-start: var(--space-3);
  }
</style>
